/**
 * Compute the *current* status of a bet against an in-play score.
 *
 * Mirrors `_evaluate_outcome` in `backend/app/services/results_evaluator.py`
 * including the Double-Chance / DNB reconcile primitives (the LLM sometimes
 * mis-labels DC picks with the wrong side; the human prediction text wins).
 *
 * Returns one of:
 *   "on_track" — bet would settle as WON if the match ended now
 *   "slipping" — bet would settle as LOST if the match ended now
 *   "tight"    — DNB push (draw refund) — bet would VOID right now
 *   null       — we don't have enough info to decide
 */

const containsAwayFirst = (text, home, away) => {
    if (!away) return false;
    if (!text.includes(away)) return false;
    if (!home || !text.includes(home)) return true;
    return text.indexOf(away) < text.indexOf(home);
};

const containsHomeFirst = (text, home, away) => {
    if (!home) return false;
    if (!text.includes(home)) return false;
    if (!away || !text.includes(away)) return true;
    return text.indexOf(home) < text.indexOf(away);
};

const reconcileDoubleChance = (pt, predText, home, away) => {
    if (!["dc_home_draw", "dc_away_draw", "dc_home_away"].includes(pt)) return pt;
    const t = (predText || "").toLowerCase();
    const h = (home || "").toLowerCase();
    const a = (away || "").toLowerCase();
    if (pt === "dc_home_draw" && containsAwayFirst(t, h, a)) return "dc_away_draw";
    if (pt === "dc_away_draw" && containsHomeFirst(t, h, a)) return "dc_home_draw";
    return pt;
};

const reconcileDnb = (pt, predText, home, away) => {
    const t = (predText || "").toLowerCase();
    const h = (home || "").toLowerCase();
    const a = (away || "").toLowerCase();
    if (pt === "dnb_home" && containsAwayFirst(t, h, a)) return "dnb_away";
    if (pt === "dnb_away" && containsHomeFirst(t, h, a)) return "dnb_home";
    return pt;
};

export function evaluatePickAgainstScore(pick, goalsHome, goalsAway) {
    if (pick == null || goalsHome == null || goalsAway == null) return null;
    const total = goalsHome + goalsAway;
    const btts = goalsHome > 0 && goalsAway > 0;
    const homeWon = goalsHome > goalsAway;
    const awayWon = goalsAway > goalsHome;
    const drew = goalsHome === goalsAway;

    const home = pick.homeTeam || "";
    const away = pick.awayTeam || "";
    const predText = pick.prediction || "";
    let pt = (pick.predictionType || "").toLowerCase();
    pt = reconcileDoubleChance(pt, predText, home, away);
    pt = reconcileDnb(pt, predText, home, away);

    const table = {
        home_win: homeWon,
        away_win: awayWon,
        draw: drew,
        over_1_5: total > 1,
        over_2_5: total > 2,
        over_3_5: total > 3,
        under_1_5: total < 2,
        under_2_5: total < 3,
        under_3_5: total < 4,
        btts_yes: btts,
        btts_no: !btts,
        dc_home_draw: homeWon || drew,
        dc_away_draw: awayWon || drew,
        dc_home_away: homeWon || awayWon,
    };
    if (pt in table) return table[pt] ? "on_track" : "slipping";

    if (pt === "dnb_home") {
        if (drew) return "tight";
        return homeWon ? "on_track" : "slipping";
    }
    if (pt === "dnb_away") {
        if (drew) return "tight";
        return awayWon ? "on_track" : "slipping";
    }

    // Loose text fallback — mirror backend.
    const tlow = predText.toLowerCase();
    const hlow = home.toLowerCase();
    const alow = away.toLowerCase();
    if (tlow.includes("over 2.5")) return total > 2 ? "on_track" : "slipping";
    if (tlow.includes("under 2.5")) return total < 3 ? "on_track" : "slipping";
    if (tlow.includes("over 1.5")) return total > 1 ? "on_track" : "slipping";
    if (tlow.includes("both teams to score") || tlow.includes("btts")) {
        return btts ? "on_track" : "slipping";
    }
    if (hlow && tlow.includes(hlow) && tlow.includes("win")) {
        return homeWon ? "on_track" : "slipping";
    }
    if (alow && tlow.includes(alow) && tlow.includes("win")) {
        return awayWon ? "on_track" : "slipping";
    }
    if (tlow.includes("draw")) return drew ? "on_track" : "slipping";
    return null;
}
